import type {
  ExternalRefundResponseType,
  OrderGetResponseType,
} from "@sellar-io/api-v2/types";
import {
  Button,
  InputField,
  InputText,
  InputTextArea,
} from "@sellar-io/components";
import { formatCurrency } from "@sellar-io/common";
import { useForm, useStore } from "@tanstack/react-form";
import { useState } from "react";
import { Alert } from "../../../components/alert/Alert";
import {
  Dialog,
  DialogCTA,
  DialogContent,
  DialogNavigation,
} from "../../../components/modal/Dialog";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useRecordOrderExternalRefundConfirmation } from "../../../hooks/api/v2/order/useOrderExternalRefundConfirmation";

export function OrderExternalRefundDialog({
  orderId,
  currency,
  buyerName,
  remainingAmount,
  refreshing,
  refreshBalance,
  onDismiss,
}: {
  orderId: number;
  currency: OrderGetResponseType["currency"];
  buyerName: string;
  remainingAmount: number;
  refreshing: boolean;
  refreshBalance: () => Promise<ExternalRefundResponseType>;
  onDismiss: () => void;
}) {
  const mutation = useRecordOrderExternalRefundConfirmation(orderId);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const form = useForm({
    defaultValues: {
      confirmedAmount: null as number | null,
      note: "",
      reference: "",
    },
    onSubmit: async ({ value }) => {
      if (value.confirmedAmount !== remainingAmount || refreshing) {
        return;
      }
      setErrorMessage(null);
      try {
        const current = await refreshBalance();
        if (current.confirmation) {
          onDismiss();
          return;
        }
        if (!current.enabled || !current.canRecord) {
          setErrorMessage(
            current.unavailableReason ??
              "External refund recording is unavailable.",
          );
          return;
        }
        if (current.remainingAmount !== value.confirmedAmount) {
          form.setFieldValue("confirmedAmount", null);
          setErrorMessage(
            "The refundable amount has changed. Review the updated amount and confirm it again.",
          );
          return;
        }
        await mutation.mutateAsync({
          confirmedRefundCompleted: true,
          expectedRemainingAmount: value.confirmedAmount,
          note: value.note.trim() || null,
          reference: value.reference.trim() || null,
        });
        onDismiss();
      } catch (error) {
        setErrorMessage(
          apiV2AssertError(error)?.response?.data.message ??
            "Unable to record the refund. Please try again.",
        );
        // A failed response may follow a committed write or a changed balance.
        try {
          await refreshBalance();
        } catch {
          // Preserve the entered details so the supplier can retry safely.
        }
      }
    },
  });
  const submitting = useStore(form.store, (state) => state.isSubmitting);
  const confirmedAmount = useStore(
    form.store,
    (state) => state.values.confirmedAmount,
  );
  const busy = submitting || mutation.isPending;

  return (
    <Dialog active onDismiss={busy ? undefined : onDismiss}>
      <DialogNavigation title="Record completed external refund" />
      <DialogContent>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <p>
            Full remaining amount:{" "}
            <strong>{formatCurrency(remainingAmount, currency)}</strong>
          </p>
          <p>
            This records a refund you have already completed for {buyerName}. It
            does not send money or update accounting software. Your confirmation
            will be used to notify the buyer.
          </p>
          {errorMessage && (
            <div role="alert">
              <Alert type="error" message={errorMessage} />
            </div>
          )}
          <form.Field name="reference">
            {(field) => (
              <InputField label="Refund reference (optional)">
                <InputText
                  name={field.name}
                  value={field.state.value}
                  maxLength={255}
                  disabled={busy}
                  onBlur={field.handleBlur}
                  onChange={(event) => field.handleChange(event.target.value)}
                />
              </InputField>
            )}
          </form.Field>
          <form.Field name="note">
            {(field) => (
              <InputField label="Supporting note (optional)">
                <InputTextArea
                  name={field.name}
                  value={field.state.value}
                  maxLength={1000}
                  disabled={busy}
                  onBlur={field.handleBlur}
                  onChange={(event) => field.handleChange(event.target.value)}
                />
              </InputField>
            )}
          </form.Field>
          <form.Field name="confirmedAmount">
            {(field) => (
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  name={field.name}
                  required
                  disabled={busy || refreshing}
                  checked={field.state.value === remainingAmount}
                  onBlur={field.handleBlur}
                  onChange={(event) =>
                    field.handleChange(
                      event.target.checked ? remainingAmount : null,
                    )
                  }
                  className="mt-1 shrink-0"
                />
                <span>
                  I confirm that I have returned{" "}
                  {formatCurrency(remainingAmount, currency)} to {buyerName} and
                  the refund has completed—not just been initiated.
                </span>
              </label>
            )}
          </form.Field>
          <DialogCTA className="flex-wrap gap-y-2 px-0">
            <Button
              color="secondary"
              type="button"
              disabled={busy}
              onClick={onDismiss}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={busy}
              disabled={
                busy || refreshing || confirmedAmount !== remainingAmount
              }
            >
              Record refund confirmation
            </Button>
          </DialogCTA>
        </form>
      </DialogContent>
    </Dialog>
  );
}
